import { useEffect, useRef, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api } from "./api.ts";
import { UndoProvider } from "./components/UndoToast.tsx";
import { useAsync } from "./hooks.ts";
import { Admin } from "./routes/Admin.tsx";
import { Board } from "./routes/Board.tsx";
import { Login } from "./routes/Login.tsx";
import { Room } from "./routes/Room.tsx";
import { Rooms } from "./routes/Rooms.tsx";
import { Scores } from "./routes/Scores.tsx";
import { Today } from "./routes/Today.tsx";
import type { Me } from "../shared/types.ts";

export function App() {
  const session = useAsync(() => api.me());
  const { pathname } = useLocation();

  if (session.loading && !session.data)
    return (
      <p className="muted" style={{ padding: "var(--space-10)" }}>
        Loading…
      </p>
    );

  const me = session.data?.me ?? null;
  if (!me) return <Login kidMode={session.data?.kidMode === true} onSignedIn={session.reload} />;

  // The kitchen board carries its own chrome: a top bar and a nav rail are
  // wasted rows on a device nobody is holding.
  if (pathname === "/board")
    return (
      <UndoProvider>
        <Board me={me} />
      </UndoProvider>
    );

  // Admin has no app chrome either. It is a workshop, and wrapping it in the
  // daily navigation is exactly what would make it feel like part of the daily
  // app. Kids and guests never reach it — enforced here and again on every
  // write on the server.
  if (pathname === "/admin") {
    return me.role === "parent" ? <Admin /> : <Navigate to="/" replace />;
  }

  return (
    <UndoProvider>
      <div className="app">
        <header className="topbar">
          <div className="topbar__inner">
            <span className="topbar__brand">
              <span className="topbar__mark" aria-hidden="true" />
              Chores
            </span>
            <span className="topbar__spacer" />
            <AccountMenu me={me} onSignedOut={session.reload} />
          </div>
        </header>

        <nav className="nav">
          <NavLink className="nav__link" to="/" end>
            <span className="nav__dot" aria-hidden="true" />
            Today
          </NavLink>
          <NavLink className="nav__link" to="/rooms">
            <span className="nav__dot" aria-hidden="true" />
            House
          </NavLink>
          <NavLink className="nav__link" to="/scores">
            <span className="nav__dot" aria-hidden="true" />
            Week
          </NavLink>
        </nav>

        <main className="app-main">
          <Routes>
            <Route path="/" element={<Today me={me} />} />
            <Route path="/rooms" element={<Rooms />} />
            <Route path="/room/:id" element={<Room me={me} />} />
            <Route path="/scores" element={<Scores />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </UndoProvider>
  );
}

/**
 * The only way into admin that is not typing the URL.
 *
 * Deliberately not a nav item: setting up the house is a rare, careful act,
 * and putting it beside Today invites someone to wander in mid-week and delete
 * a floor. It sits behind your own name, where you already go to sign out.
 */
function AccountMenu({ me, onSignedOut }: { me: Me; onSignedOut: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="account" ref={ref}>
      <button
        type="button"
        className="account__button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {me.name}
      </button>

      {open ? (
        <div className="account__menu" role="menu">
          <div className="account__note">
            Signed in as {me.name}
            {me.role === "parent" ? "" : ` · ${me.role}`}
          </div>
          {me.role === "parent" ? (
            <button className="account__item" role="menuitem" onClick={() => navigate("/admin")}>
              Set up the house
            </button>
          ) : null}
          <button
            className="account__item"
            role="menuitem"
            onClick={() => {
              void api.logout().then(onSignedOut);
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
